import React from "react"
import { Query } from "react-apollo"
import { Redirect } from "react-router-dom"
import PDFView from "../../components/PDFView"
import Button from "../../components/Button"
import qs from "qs"
import gql from "graphql-tag"
import AppContext from "../../shared/AppContext"

import "./viewAiProductSeries.scss"
import { MarketArrayReport } from "../MarketArray"

const fileUrlFragment = gql`
  fragment fileUrlFragment on AiProductSeries {
    fileUrl(type: $type, slug: $slug)
  }
`

const GET_AI_PRODUCT_SERIES = gql`
  query GetAiProductSeries(
    $bundleSlug: String!
    $slug: String!
    $type: String
  ) {
    getAiProductSeries(bundleSlug: $bundleSlug, slug: $slug, type: $type) {
      id
      name
      slug
      mp4File {
        name
        slug
        fileName
      }
      reportFile {
        name
        slug
        fileName
      }
      dataFile {
        name
        slug
        fileName
      }
      pdfFile {
        name
        slug
        fileName
      }
      pdfFiles {
        name
        slug
      }
      ...fileUrlFragment
    }
  }
  ${fileUrlFragment}
`

const ViewAiProductSeries = (props) => {
  const { viewer } = props

  const { trackEvent } = React.useContext(AppContext)

  if (!viewer.isUserAuthenticated) {
    return (
      <Redirect
        to={{ pathname: "/login", state: { from: "/ai-product-series" } }}
      />
    )
  }

  let { type } = qs.parse(props.location.search, {
    ignoreQueryPrefix: true,
  })

  const accessRoles = [
    "AiProductSeries",
    "ETR Master Admin",
    "ETR Research Admin",
    "ETR Sales Admin",
  ]

  const hasAccessRole = accessRoles.some((role) => viewer.roles.includes(role))

  const onDownloadClick = (series) => {
    trackEvent("Downloaded AI Product Series", {
      series: series.name,
      seriesId: series.id,
      file: props.match.params.slug,
    })
  }

  const onVideoDownloadClick = async (series) => {
    onDownloadClick(series)

    try {
      const response = await fetch(series.fileUrl, { method: "GET" })
      const blob = await response.blob()

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")

      link.href = url
      link.download = series.mp4File.fileName

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error("Error downloading file:", error)
    }
  }

  const goBack = (e) => {
    e.preventDefault()
    props.history.goBack()
  }

  const goHome = (e) => {
    e.preventDefault()
    props.history.push("/")
  }

  const renderFile = (series) => {
    if (!series) {
      return <div>No AI Product Series found.</div>
    }

    if (type !== "pdf" && type !== "mp4") {
      return <div>Unknown file type.</div>
    }

    if (type === "mp4") {
      return (
        <div className="video-container">
          <video autoPlay={false} controls style={{ maxHeight: "100%" }}>
            <source src={series.fileUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <h2>
            {`${series.name} -
            ${series.mp4File.name}`}
          </h2>
        </div>
      )
    }

    return <PDFView url={series.fileUrl} withCredentials={true} />
  }

  const renderDownloadButton = (series) => {
    if (!series || !series.fileUrl || (type !== "pdf" && type !== "mp4")) {
      return null
    }

    if (type === "mp4") {
      return (
        <div
          className="btn btn-download"
          data-cy="view-video-download-button"
          onClick={() => onVideoDownloadClick(series)}
        >
          <span className="download-here glyphicon glyphicon-download-alt" />
        </div>
      )
    }

    return (
      <Button
        data-cy="view-pdf-download-button"
        href={`${series.fileUrl}?mode=download`}
        download={`${series.fileUrl}?mode=download`}
        classes={["btn-download"]}
        action={() => onDownloadClick(series)}
      >
        <span className="glyphicon glyphicon-download-alt" />
      </Button>
    )
  }

  return (
    <Query
      query={GET_AI_PRODUCT_SERIES}
      variables={{
        bundleSlug: props.match.params.bundleSlug,
        slug: props.match.params.slug,
        type: type,
      }}
      fetchPolicy="network-only"
    >
      {({ loading, error, data }) => {
        if (loading) return <div>Loading...</div>
        if (error) return <div>Error while getting file.</div>

        const series = data && data.getAiProductSeries

        if (type === "html") {
          return (
            <MarketArrayReport
              {...props}
              role="AiProductSeries"
              from="/ai-product-series"
            />
          )
        }

        return (
          <div className="view-ai-product-series-container">
            <div className="top-options">
              <div className="links">
                <a onClick={goBack} data-cy="back-link">
                  <span className="glyphicon glyphicon-menu-left" />
                  Go Back
                </a>
                <a onClick={goHome} data-cy="home-link">
                  <span className="glyphicon glyphicon-home" />
                  Home
                </a>
              </div>

              {hasAccessRole && !viewer.isFreeTrial
                ? renderDownloadButton(series)
                : null}
            </div>

            {!hasAccessRole ? <div> Not authorized.</div> : renderFile(series)}
          </div>
        )
      }}
    </Query>
  )
}

export default ViewAiProductSeries
